import { useEffect, useRef, useState, useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'

interface SessionData {
  session_id: number
  session_date: string
  active_seconds: number
  completed_minimum: boolean
  daily_minimum_seconds: number
  progress_pct: number
  xp_earned_today: number
  scans_today: number
  streak: {
    current_streak: number
    longest_streak: number
    multiplier: number
    streak_extended: boolean
    streak_broken: boolean
    shield_used: boolean
    weekly_day: number
  }
  daily_bonus: {
    day_in_cycle: number
    xp_awarded: number
    multiplier: number
    message: string
  } | null
}

interface HeartbeatResult {
  session_id: number
  active_seconds: number
  idle_seconds: number
  completed_minimum: boolean
  progress_pct: number
  remaining_seconds: number
  completion_result: {
    xp_awarded: number
    multiplier: number
    shield_earned: boolean
    message: string
  } | null
  extension_bonus: {
    threshold_minutes: number
    xp_awarded: number
    message: string
  } | null
}

interface EngagementState {
  sessionActive: boolean
  activeSeconds: number
  progressPct: number
  completedMinimum: boolean
  remainingSeconds: number
  currentStreak: number
  multiplier: number
  scansToday: number
  xpEarnedToday: number
}

const HEARTBEAT_INTERVAL = 30000 // 30 seconds
const IDLE_TIMEOUT = 120000 // 2 minutes

export function useEngagement() {
  const queryClient = useQueryClient()
  const [isActive, setIsActive] = useState(false)
  const [lastActivity, setLastActivity] = useState(Date.now())
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null)
  const idleCheckRef = useRef<NodeJS.Timeout | null>(null)

  // Engagement state
  const [state, setState] = useState<EngagementState>({
    sessionActive: false,
    activeSeconds: 0,
    progressPct: 0,
    completedMinimum: false,
    remainingSeconds: 15 * 60,
    currentStreak: 0,
    multiplier: 1.0,
    scansToday: 0,
    xpEarnedToday: 0,
  })

  // Notifications for bonuses
  const [notifications, setNotifications] = useState<string[]>([])

  const addNotification = useCallback((message: string) => {
    setNotifications(prev => [...prev, message])
    setTimeout(() => {
      setNotifications(prev => prev.slice(1))
    }, 5000)
  }, [])

  // Start session mutation
  const startSessionMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/api/student/session/start')
      return response.data as SessionData
    },
    onSuccess: (data) => {
      setState(prev => ({
        ...prev,
        sessionActive: true,
        activeSeconds: data.active_seconds,
        progressPct: data.progress_pct,
        completedMinimum: data.completed_minimum,
        remainingSeconds: data.daily_minimum_seconds - data.active_seconds,
        currentStreak: data.streak.current_streak,
        multiplier: data.streak.multiplier,
        scansToday: data.scans_today,
        xpEarnedToday: data.xp_earned_today,
      }))

      // Show notifications
      if (data.daily_bonus) {
        addNotification(`${data.daily_bonus.message} +${data.daily_bonus.xp_awarded} XP`)
      }
      if (data.streak.streak_extended && data.streak.current_streak > 1) {
        addNotification(`Streak extended to ${data.streak.current_streak} days!`)
      }
      if (data.streak.shield_used) {
        addNotification(`Shield used! Your streak is protected.`)
      }
    },
  })

  // Heartbeat mutation
  const heartbeatMutation = useMutation({
    mutationFn: async (active: boolean) => {
      const response = await api.post('/api/student/session/heartbeat', { active })
      return response.data as HeartbeatResult
    },
    onSuccess: (data) => {
      setState(prev => ({
        ...prev,
        activeSeconds: data.active_seconds,
        progressPct: data.progress_pct,
        completedMinimum: data.completed_minimum,
        remainingSeconds: data.remaining_seconds,
      }))

      // Show completion notification
      if (data.completion_result) {
        addNotification(data.completion_result.message)
        if (data.completion_result.shield_earned) {
          addNotification('You earned a streak shield!')
        }
        // Invalidate dashboard to refresh XP
        queryClient.invalidateQueries({ queryKey: ['student-dashboard'] })
      }

      // Show extension bonus
      if (data.extension_bonus) {
        addNotification(`${data.extension_bonus.message} +${data.extension_bonus.xp_awarded} XP`)
        queryClient.invalidateQueries({ queryKey: ['student-dashboard'] })
      }
    },
  })

  // End session mutation
  const endSessionMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/api/student/session/end')
      return response.data
    },
    onSuccess: () => {
      setState(prev => ({
        ...prev,
        sessionActive: false,
      }))
    },
  })

  // Track user activity
  const recordActivity = useCallback(() => {
    setLastActivity(Date.now())
    if (!isActive) {
      setIsActive(true)
    }
  }, [isActive])

  // Check for idle state
  const checkIdle = useCallback(() => {
    const now = Date.now()
    const timeSinceActivity = now - lastActivity
    if (timeSinceActivity > IDLE_TIMEOUT && isActive) {
      setIsActive(false)
    }
  }, [lastActivity, isActive])

  // Start tracking engagement
  const startTracking = useCallback(() => {
    if (!state.sessionActive) {
      startSessionMutation.mutate()
    }
  }, [state.sessionActive, startSessionMutation])

  // Stop tracking engagement
  const stopTracking = useCallback(() => {
    if (state.sessionActive) {
      endSessionMutation.mutate()
    }
  }, [state.sessionActive, endSessionMutation])

  // Set up heartbeat interval
  useEffect(() => {
    if (state.sessionActive) {
      // Send heartbeat every 30 seconds
      heartbeatRef.current = setInterval(() => {
        heartbeatMutation.mutate(isActive)
      }, HEARTBEAT_INTERVAL)

      // Check for idle every 10 seconds
      idleCheckRef.current = setInterval(checkIdle, 10000)

      // Clean up on unmount
      return () => {
        if (heartbeatRef.current) clearInterval(heartbeatRef.current)
        if (idleCheckRef.current) clearInterval(idleCheckRef.current)
      }
    }
  }, [state.sessionActive, isActive, heartbeatMutation, checkIdle])

  // Listen for user activity
  useEffect(() => {
    const handleActivity = () => recordActivity()

    window.addEventListener('mousemove', handleActivity)
    window.addEventListener('keydown', handleActivity)
    window.addEventListener('click', handleActivity)
    window.addEventListener('scroll', handleActivity)
    window.addEventListener('touchstart', handleActivity)

    return () => {
      window.removeEventListener('mousemove', handleActivity)
      window.removeEventListener('keydown', handleActivity)
      window.removeEventListener('click', handleActivity)
      window.removeEventListener('scroll', handleActivity)
      window.removeEventListener('touchstart', handleActivity)
    }
  }, [recordActivity])

  // Auto-start session on mount
  useEffect(() => {
    startTracking()

    // End session on unmount
    return () => {
      if (state.sessionActive) {
        api.post('/api/student/session/end').catch(() => {})
      }
    }
  }, [])

  return {
    ...state,
    isActive,
    notifications,
    startTracking,
    stopTracking,
    recordActivity,
  }
}
